import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { isOtcWrapper } from 'models/wrapper';
import type {
  ITradeAllocationLine,
  ITradeAllocationRequestModel,
} from 'routes/quote/trade-allocations/trade-allocations.types';
import type { AppState } from 'state/state.model';

import type { ValidationError } from './tradeAllocationForm.model';

const getTab = (state: AppState, tabId: string) => state.tradeAllocationForm[tabId];

const getTradeAllocationForm = (state: AppState, tabId: string) => getTab(state, tabId)?.form;

const getInfo = (state: AppState, tabId: string) => getTab(state, tabId)?.info;
export function getApplyStatus(state: AppState, tabId: string) {
  return getTab(state, tabId)?.applyStatus;
}
export function getApplyRequestStartingTimeMs(state: AppState, tabId: string) {
  return getTab(state, tabId)?.applyStartingTimeMs;
}
export function getApplyRequestEstimatedDurationTimeInMs(state: AppState, tabId: string) {
  return getTab(state, tabId)?.applyEstimatedDurationTimeMs;
}
export function getApplyResponse(state: AppState, tabId: string) {
  return getTab(state, tabId)?.applyResponse;
}
export function getApplyError(state: AppState, tabId: string) {
  return getTab(state, tabId)?.applyError;
}
export function getLoadingStatus(state: AppState, tabId: string) {
  return getTab(state, tabId)?.loadingStatus;
}
export function getFightingBackJustification(state: AppState, tabId: string) {
  return getTab(state, tabId)?.fightingBackJustification;
}
export function getFightingBackComment(state: AppState, tabId: string) {
  return getTab(state, tabId)?.fightingBackComment;
}

export function getCurrency(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.currency;
}
export function getSponso(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.sponso;
}
export function getSponsoPercentUpFront(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.sponsoPercentUpFront;
}
export function getF2Y(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.f2Y;
}
export function getTopUpPercentUpFront(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.topUpPercentUpFront;
}

export function getAllocationDetails(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.allocationDetails;
}

export function getCustodians(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.custodians;
}

export function getEtag(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.etag;
}

export function getProductReference(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.productReference;
}

export function getSalesOwner(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.salesOwner;
}
export function getNotional(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.notional;
}
export function getOfferPrice(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.offerPrice;
}
export function getTradingPrice(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.tradingPrice;
}
export function getUpfrontFee(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.upfrontFee;
}
export function getXMargin(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.salesMargin;
}
export function getTradeWay(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.tradeWay;
}
export function getMonetarySchedule(state: AppState, tabId: string) {
  return getTradeAllocationForm(state, tabId)?.monetarySchedule;
}
export function getTradeId(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.tradeId;
}
export function getProduct(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.product;
}
export function getWrapper(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.wrapper;
}

export function getIsOtc(state: AppState, tabId: string) {
  const wrapper = getInfo(state, tabId)?.wrapper;
  return isOtcWrapper(wrapper);
}

export function getFileCode(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.fileCode;
}
export function getIsin(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.isin;
}
export function getProductId(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.productId;
}

export function getRfqId(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.rfqId;
}

export function getSelectedOnBehalfClient(state: AppState, tabId: string): ISalesCounterpart | null {
  const client = getTradeAllocationForm(state, tabId)?.client;

  if (client === undefined) {
    return null;
  }
  return {
    id: client.bdrId ?? '',
    label: client.name ?? '',
  };
}

export function getApplyRequest(state: AppState, tabId: string): ITradeAllocationRequestModel {
  const initialAllocations = getTab(state, tabId).initialAllocations;
  const allocations: ITradeAllocationLine[] = getAllocationDetails(state, tabId)!.map((x, index) => {
    const initialAllocation = initialAllocations?.[index];
    const isUpdated =
      x.tradeDate !== initialAllocation?.tradeDate ||
      x.custodianCode !== initialAllocation?.custodianCode ||
      x.notional !== initialAllocation?.notional ||
      x.offerPrice !== initialAllocation?.offerPrice ||
      x.distributorUpFront !== initialAllocation?.distributorUpFront ||
      x.custodianUpFront !== initialAllocation?.custodianUpFront ||
      x.salesMargin !== initialAllocation?.salesMargin ||
      x.valueDate !== initialAllocation?.valueDate;
    return {
      CustodianBdrId: x.custodianCode!,
      CustodianUpFront: x.custodianUpFront!,
      DealId: x.dealId!,
      DistributorUpFront: x.distributorUpFront!,
      Notional: x.notional!,
      OfferPrice: x.offerPrice!,
      TradeDate: x.tradeDate!,
      IsNotionalInUnit: x.isNotionalInUnit,
      NotionalInUnit: x.notionalInUnit,
      SalesMargin: x.salesMargin,
      ValueDate: x.valueDate,
      IsUpdated: isUpdated,
      IndependentAmount: x.independentAmount
        ? {
            Type: x.independentAmount.type,
            Amount: x.independentAmount.amount,
            PercentageAmount: x.independentAmount.percentageAmount,
            ValueDate: x.independentAmount.valueDate,
            Payer: x.independentAmount.payer ?? '',
          }
        : undefined,
    };
  });
  return {
    Comment: getFightingBackComment(state, tabId)!,
    FightingBackJustificationId: getFightingBackJustification(state, tabId)!,
    OfferPrice: getOfferPrice(state, tabId)!,
    ProductReference: getProductReference(state, tabId)!,
    SalesCounterPartBdrId: getSelectedOnBehalfClient(state, tabId)!.id,
    SalesMargin: getXMargin(state, tabId)!,
    SalesOwner: getSalesOwner(state, tabId)!,
    TradingPrice: getTradingPrice(state, tabId)!,
    UpfrontFee: getUpfrontFee(state, tabId)!,
    Sponso: getSponso(state, tabId)!,
    F2Y: getF2Y(state, tabId)!,
    SponsoPercentUpFront: getSponsoPercentUpFront(state, tabId)!,
    TopUpPercentUpFront: getTopUpPercentUpFront(state, tabId)!,
    AllocationRows: allocations,
    Etag: getEtag(state, tabId)!,
    Notional: getNotional(state, tabId)!,
  };
}

export function getUnallocatedNotional(state: AppState, tabId: string) {
  const allocations = getAllocationDetails(state, tabId) ?? [];
  const notional = getNotional(state, tabId);
  const allocatedNotional = allocations.reduce((acc, cur) => acc + (cur.notional || 0), 0);
  return (notional || 0) - allocatedNotional;
}

export function isReadyToApplyTradeAllocations(state: AppState, tabId: string) {
  const fightingBackJustification = getFightingBackJustification(state, tabId);
  const hasValidationErrors = getValidationErrors(state, tabId).some(e => e.severity === 'Error');
  return fightingBackJustification !== undefined && fightingBackJustification !== '' && !hasValidationErrors;
}

export function isPrecomPeriodNotStarted(state: AppState, tabId: string) {
  const monetarySchedule = getMonetarySchedule(state, tabId);

  if (!monetarySchedule) {
    return false;
  }
  return monetarySchedule.preComStatus === 'NOT_STARTED';
}

export function isPrecomPeriodClosed(state: AppState, tabId: string) {
  const monetarySchedule = getMonetarySchedule(state, tabId);

  if (!monetarySchedule) {
    return false;
  }
  return monetarySchedule.preComStatus === 'CLOSED';
}

export function isPrecomPeriodOpen(state: AppState, tabId: string) {
  const monetarySchedule = getMonetarySchedule(state, tabId);

  if (!monetarySchedule) {
    return false;
  }
  return monetarySchedule.preComStatus === 'OPEN';
}

export function isReadonly(state: AppState, tabId: string) {
  const applyStatus = getTab(state, tabId)?.applyStatus;
  const loadingStatus = getTab(state, tabId)?.loadingStatus;
  return applyStatus !== 'Initialized' || loadingStatus === 'Error';
}

export function isLoading(state: AppState, tabId: string) {
  const loadingStatus = getTab(state, tabId)?.loadingStatus;
  return loadingStatus === undefined || loadingStatus === 'Pending' || loadingStatus === 'Resetting';
}

export function getValidationErrors(state: AppState, tabId: string): ReadonlyArray<ValidationError> {
  if (isLoading(state, tabId)) {
    return [];
  }

  const loadingStatus = getLoadingStatus(state, tabId);

  if (loadingStatus === 'Error') {
    const loadingError = getTab(state, tabId)?.loadingError;
    return [
      {
        fields: [],
        translationKey: loadingError as any,
        severity: 'Error',
        values: {},
      },
    ];
  }

  const errors: Array<ValidationError> = [];

  if (getUnallocatedNotional(state, tabId) === undefined || getUnallocatedNotional(state, tabId)! !== 0) {
    errors.push({
      fields: [],
      translationKey: 'tradeAllocation.error.unallocatedNotional',
      severity: 'Error',
      values: {},
    });
  }

  if (
    getNotional(state, tabId) === undefined ||
    getNotional(state, tabId) === null ||
    getAllocationDetails(state, tabId)?.some(x => x.notional === null)
  ) {
    errors.push({
      fields: [],
      translationKey: 'tradeAllocation.error.notional.notDefined',
      severity: 'Error',
      values: {},
    });
  }

  if (getSelectedOnBehalfClient(state, tabId) === null) {
    errors.push({
      fields: [],
      translationKey: 'tradeAllocation.error.salesCounterpart.notDefined',
      severity: 'Error',
      values: {},
    });
  }

  if (getAllocationDetails(state, tabId)?.some(x => x.custodianCode === undefined)) {
    errors.push({
      fields: [],
      translationKey: 'tradeAllocation.error.custodian.notDefined',
      severity: 'Error',
      values: {},
    });
  }

  if (
    getAllocationDetails(state, tabId)?.some(
      x => x.independentAmount?.type === 'Percentage' && x.independentAmount.percentageAmount === null,
    )
  ) {
    errors.push({
      fields: [],
      translationKey: 'tradeAllocation.error.independentAmount.percentage.notDefined' as any,
      severity: 'Error',
      values: {},
    });
  }

  if (
    getAllocationDetails(state, tabId)?.some(
      x => x.independentAmount?.type === 'Notional' && x.independentAmount.amount === null,
    )
  ) {
    errors.push({
      fields: [],
      translationKey: 'tradeAllocation.error.independentAmount.amount.notDefined' as any,
      severity: 'Error',
      values: {},
    });
  }

  if (
    (getFightingBackJustification(state, tabId) === '' ||
      getFightingBackJustification(state, tabId) === null ||
      getFightingBackJustification(state, tabId) === undefined) &&
    !isReadonly(state, tabId) &&
    !isPrecomPeriodClosed(state, tabId) &&
    !isPrecomPeriodNotStarted(state, tabId)
  ) {
    errors.push({
      fields: [],
      translationKey: 'tradeAllocation.error.fightingbackJustification.notDefined',
      severity: 'Error',
      values: {},
    });
  }

  const warnings = getApplyResponse(state, tabId)?.warnings;
  if (warnings) {
    warnings.forEach(warning => {
      errors.push({
        fields: [],
        translationKey: warning as any,
        severity: 'Warning',
        values: {},
      });
    });
  }

  if (isPrecomPeriodNotStarted(state, tabId)) {
    errors.push({
      fields: [],
      translationKey: 'tradeAllocation.warning.precomNotStarted',
      severity: 'Error',
      values: {},
    });
  }

  if (isPrecomPeriodClosed(state, tabId)) {
    errors.push({
      fields: [],
      translationKey: 'tradeAllocation.warning.precomClosed',
      severity: 'Error',
      values: {},
    });
  }

  if (isPrecomPeriodOpen(state, tabId) && !getMonetarySchedule(state, tabId)?.preComPrice) {
    errors.push({
      fields: [],
      translationKey: 'tradeAllocation.error.precomPrice.unavailable',
      severity: 'Warning',
      values: {},
    });
  }

  return errors;
}
